import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Box, Stack, Typography } from '@mui/material';

import { BreadCrumbs } from 'components/Breadcrumbs';
// import { ContentLayout } from 'components/Layout';
import { ContentLayout } from 'website/components/Layout/components/ContentLayout';

import { paths, withLocale } from 'routes';
import { useLocale } from 'i18n/useLocale';
import { DELEGON_URL } from 'configuration';

import { DetailViewTabList } from 'components/TabList';
import { DetailsTab } from '../components/JobsDetailsTab';
import { Loader } from 'components/Loader';
import { useJobAdBySlug } from 'website/api/getJobAdBySlug';
import { useTrackVisit } from 'hook/useTrackVisit';
import { Subscribe } from 'website/components/JobAdsSubscribeSection';
import BackgroundImage from '../assets/background.png';

export const DetailsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const currentTab = queryParams.get('tab') || 'details';
  const [ tab, setTab ] = useState(currentTab);  
  const [ loading, setLoading ] = useState(false);
  const [timer, setTimer] = useState(null);
  const [ meta, setMeta ] = useState([]);
  let { slug } = useParams();
  const lang = useLocale();
  const { t } = useTranslation();

  const { data: jobAd, refetch: refetchAd, error, isError, isLoading: jobAdLoading, isFetched: jobAdFetched } = useJobAdBySlug(slug);   
  useTrackVisit('job_ad', jobAd?.ad_id, 'website');
  
  const switchTab = (_, newTab) => {
    navigate(`?tab=${newTab}`, { replace: true });
  };

  const isLoading = jobAdLoading;
  const isFetched = jobAdFetched;
  useEffect(() => {  
    if (isLoading) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isFetched) {    
    if (loading) {      
      setTimeout(() => setLoading(false), 1000);
    }
  }

  useEffect(() => {
    setTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    if (jobAd) {
      setMeta(
        [
          { name: 'description', content: jobAd?.description },
          { property: 'og:title', content: `Delegon | ${jobAd?.headline}` },
          { property: 'og:description', content: jobAd?.description },
          { property: 'og:image', content: jobAd?.buyer_logo_url },
          { property: 'og:url', content: `${DELEGON_URL}${paths.public.jobAdsDetailsView(jobAd?.slug, lang)}` },
          { property: 'og:type', content: 'article' },
          { property: 'og:publish_date', content: jobAd?.created_on },
        ]
      );
    }
  }, [jobAd, lang])

  if (!loading && isError && error.message === "Object doesn't exist") {
    return <div>{error?.message}</div>;
  }

  return (
    <ContentLayout meta={meta} title={jobAd?.headline || "Delegon"} hreflang={false}>
      {isLoading && !loading ? (
        <></>
      )
      : (
        loading ? (
          <Loader style={{marginTop: "40vh"}}/>
        )
        : (
          !loading && isError ? (
            <div>{t('website:jobDetails.loadError')}</div>
          ) : (
            <Stack height="100%">
              <BreadCrumbs
                items={[
                  { name: '', to: withLocale(paths.website.jobAds, lang) },
                  { name: t('website:jobDetails.breadcrumb'), to: withLocale(paths.website.jobAds, lang) },
                  { name: jobAd?.headline ?? t('website:jobDetails.breadcrumbFallback') },
                ]}
              />
              <Stack
                px={4}
                py={2}
                bgcolor="background.paper"
                borderTop="1px solid"
                borderColor="border.main"
                gap={1}
                height="100%"
              >
                {!error && (
                  <>
                    <DetailsTab currentTab={tab} jobAd={jobAd} />
                    {/* <FilesTab currentTab={tab} boosted={jobAd?.promoted} /> */}
                  </>
                )}
              </Stack>
              <Stack
                sx={{
                  backgroundImage: `url(${BackgroundImage})`,
                  backgroundColor: 'grey.300',
                  backgroundPosition: 'center',
                  backgroundSize: "cover",
                  backgroundRepeat: 'no-repeat',
                  position: "relative",
                  // The parent Stack centers children that are wider
                  // than it (align-items: center), which throws off the
                  // left:50% / margin-left:-50vw full-bleed trick on
                  // mobile. Stretching this box to the parent's start
                  // edge keeps that math correct.
                  alignSelf: "flex-start",
                  ml: "-50vw",
                  left: "50%",
                  width: "100vw",
                }}
              >
                <Stack
                  width={1}
                  maxWidth={{xs:664, md: 1200}}
                  mx="auto"
                  boxSizing="border-box"
                  px={{xs: 4, md: 6}}
                  sx={{
                    // Subscribe's own top/bottom padding is uneven
                    // (30px/50px); shift it down by half the difference
                    // so it's visually centered in the picture without
                    // changing the total height.
                    mt: 1.25,
                    mb: -1.25,
                  }}
                >
                  <Subscribe />
                </Stack>
              </Stack>
            </Stack>
          )
        )
      )}
    </ContentLayout>
  );
};
